/**
 * CRO-WEB-176 Experiment Hook
 *
 * Hook that determines the subtitle text for CRO-WEB-176 experiment.
 * Tests different personalisation subheader text on preference selection steps.
 */

import { useExperimentTracking } from "@/src/experiments/useExperimentTracking";
import { logger } from "@/src/utils/logger";
import { croWeb176Config } from "./config";
import { useCroWeb176Variant } from "./getCroWeb176Variant";

export interface CroWeb176ExperimentConfig {
  shouldShowSubtitle: boolean;
  variant: string | null;
  isLoading: boolean;
}

/**
 * Hook that determines whether to show the subtitle for CRO-WEB-176 experiment
 */
export const useCroWeb176Experiment = (): CroWeb176ExperimentConfig => {
  const { variant, isInitialized } = useCroWeb176Variant();

  // Track experiment participation
  useExperimentTracking(croWeb176Config);

  // Determine if we're still loading the variant
  const isLoading = false;

  // DEBUG: Log the variant value (not in production)
  logger.log(
    "🧪 CRO-WEB-176 Experiment variant:",
    variant,
    "typeof:",
    typeof variant,
    "isLoading:",
    isLoading
  );

  // Determine whether to show subtitle based on variant
  let shouldShowSubtitle: boolean;

  switch (variant) {
    case "control":
      // Control = hidden subtitle
      shouldShowSubtitle = false;
      break;
    case "test":
      // Test = show subtitle
      shouldShowSubtitle = true;
      break;
    default:
      // No variant assigned or unknown variant - default to hidden (control)
      logger.log(
        "🧪 CRO-WEB-176 Unknown variant, defaulting to control (hidden):",
        variant
      );
      shouldShowSubtitle = false;
      break;
  }

  return {
    shouldShowSubtitle,
    variant,
    isLoading,
  };
};
